import { Fragment, Node, Slice } from 'prosemirror-model'
import { EditorState, Plugin, PluginKey } from 'prosemirror-state'
import { DecorationSet } from 'prosemirror-view'

import { constants } from '@showrunner/prose-schemas'

import type { MoresContds } from '#util/formats'
import { getPageLayout } from '#util/PageLayout'
import { PageCounts } from '#util/pagination'

import { getConfigData } from '../configData'
import {
  buildScriptContext,
  type PageBreakScriptInfo,
} from '../inlinePageBreaks/inlinePageBreaksPlugin'

import { buildDecorationsFromPageDecoration } from './page-deco-adapter'
import { cleanLockedPageBreak } from './pageBreakMarkers'
import { PageLockMap } from './PageLockMap'
import { PaginationPolicyFactory } from './PaginationPolicies'
import {
  type BasePage,
  NodePagination,
  type Page,
  Paginator,
  SimplePaginator,
} from './Paginator'
import { getSplitPoints } from './split-points'

const { NodeGroupMap } = constants

export interface IndexedPage extends BasePage {
  /** the 0-based index of the page in the document */
  index: number
}

export class ScriptPagination implements PageCounts {
  readonly pageCount: number
  readonly titlePageCount: number
  private readonly bodyPages: BasePage[]
  private readonly titlePageList: BasePage[]

  constructor(private nodePaginations: NodePagination[]) {
    const bodyPages: BasePage[] = []
    const titlePageList: BasePage[] = []

    for (const { isTitlePage, pages } of this.nodePaginations) {
      if (isTitlePage) titlePageList.push(...pages)
      else bodyPages.push(...pages)
    }

    this.bodyPages = bodyPages
    this.titlePageList = titlePageList
    this.pageCount = bodyPages.length
    this.titlePageCount = titlePageList.length
  }

  pageAt(pos: number): IndexedPage | null {
    let low = 0
    let high = this.bodyPages.length - 1

    while (low <= high) {
      const index = Math.floor((low + high) / 2)
      const page = this.bodyPages[index]

      if (pos < page.start) {
        high = index - 1
      } else if (pos >= page.end) {
        low = index + 1
      } else {
        return { ...page, index }
      }
    }

    return null
  }

  get pages(): BasePage[] {
    return this.bodyPages
  }

  get titlePages(): BasePage[] {
    return this.titlePageList
  }

  // invariant: there is always one non title paginated page at the end of the document, so we can check for shortfall there
  get lastPageShortfall(): number | null {
    for (let i = this.nodePaginations.length - 1; i >= 0; i--) {
      const { isTitlePage, pages } = this.nodePaginations[i]
      if (pages.length > 0 && !isTitlePage) {
        const lastIndex = pages.length - 1
        return pages[lastIndex].shortfall
      }
    }

    return null
  }

  [Symbol.iterator]() {
    return this.nodePaginations[Symbol.iterator]()
  }
}

interface PaginationPluginState {
  paginator: Paginator
  pagination: ScriptPagination
  locking: PageLockMap
  decorations: DecorationSet
}

const paginationKey = new PluginKey<PaginationPluginState>('pagination')

export function createPaginationPlugin(
  scriptInfo?: PageBreakScriptInfo,
  isSplitPointDebugEnabled?: () => boolean,
  suppressDecorations = false,
): Plugin<PaginationPluginState> {
  return new Plugin<PaginationPluginState>({
    key: paginationKey,
    providesPagination: true,
    state: {
      init(_config, editorState) {
        return computePaginationPluginState(
          editorState,
          scriptInfo,
          isSplitPointDebugEnabled,
        )
      },
      apply(tr, pluginState, _oldState, newState) {
        if (!tr.docChanged && !tr.getMeta('choo')) return pluginState
        const { currentMoresContds } = getConfigData(newState)

        const { blockSplitPoints, inlineSplitPoints } = getSplitPoints(
          newState.doc,
          isDialogueContinuationEnabled(currentMoresContds),
          scriptInfo?.scriptType,
        )
        const policy = PaginationPolicyFactory.create({
          moreContd: currentMoresContds,
          blockSplitPoints,
          inlineSplitPoints,
          scriptType: scriptInfo?.scriptType,
        })
        const paginationStates = findPaginatedNodes(newState.doc).map(
          ({ pos, node }) => pluginState.paginator.apply(pos, node, policy),
        )
        const pagination = new ScriptPagination(paginationStates)
        const locking = PageLockMap.from(pagination.pages, newState.doc)
        let decorations = buildDecorationsFromPageDecoration(
          pagination,
          locking,
          newState,
          scriptInfo ? buildScriptContext(newState.doc, scriptInfo) : undefined,
          currentMoresContds,
        )

        const splitPoints = [
          ...blockSplitPoints.createDecorations(),
          ...inlineSplitPoints.createDecorations(),
        ]
        if (isSplitPointDebugEnabled?.()) {
          decorations = decorations.add(newState.doc, splitPoints)
        }

        return {
          paginator: pluginState.paginator,
          pagination,
          locking,
          decorations,
        }
      },
    },
    props: {
      decorations(state) {
        if (suppressDecorations) return DecorationSet.empty
        return paginationKey.getState(state)?.decorations ?? DecorationSet.empty
      },
      transformPasted: stripLockedPageBreaksFromSlice,
    },
  })
}

function computePaginationPluginState(
  editorState: EditorState,
  scriptInfo?: PageBreakScriptInfo,
  isSplitPointDebugEnabled?: () => boolean,
): PaginationPluginState {
  const {
    currentBlockFormats: blockFormats,
    currentMoresContds,
    currentPageConfig: pageConfig,
  } = getConfigData(editorState)
  const { blockSplitPoints, inlineSplitPoints } = getSplitPoints(
    editorState.doc,
    isDialogueContinuationEnabled(currentMoresContds),
    scriptInfo?.scriptType,
  )
  const policy = PaginationPolicyFactory.create({
    moreContd: currentMoresContds,
    blockSplitPoints,
    inlineSplitPoints,
    scriptType: scriptInfo?.scriptType,
  })
  const paginator = SimplePaginator.from(
    blockFormats,
    getPageLayout(pageConfig),
  )
  const paginationStates = findPaginatedNodes(editorState.doc).map(
    ({ pos, node }) => paginator.init(pos, node, policy),
  )
  const pagination = new ScriptPagination(paginationStates)
  const locking = PageLockMap.from(pagination.pages, editorState.doc)
  let decorations = buildDecorationsFromPageDecoration(
    pagination,
    locking,
    editorState,
    scriptInfo ? buildScriptContext(editorState.doc, scriptInfo) : undefined,
    currentMoresContds,
  )
  const splitPoints = [
    ...blockSplitPoints.createDecorations(),
    ...inlineSplitPoints.createDecorations(),
  ]

  if (isSplitPointDebugEnabled?.()) {
    decorations = decorations.add(editorState.doc, splitPoints)
  }

  return {
    paginator,
    pagination,
    locking,
    decorations,
  }
}

function findPaginatedNodes(doc: Node): Array<{ node: Node; pos: number }> {
  const found: Array<{ node: Node; pos: number }> = []

  doc.forEach((child, pos) => {
    if (child.type.isInGroup(NodeGroupMap.LAYOUT)) {
      found.push({ node: child, pos })
    }
  })

  return found
}

function isDialogueContinuationEnabled(moresContds?: MoresContds): boolean {
  return (
    moresContds?.enableDialogueMores === true &&
    moresContds?.enableDialogueContds === true
  )
}

// for testing purposes only.
export function createStubPaginationPlugin(
  document: Node,
  pages: Page[],
): Plugin<PaginationPluginState> {
  const pageNode = document.firstChild
  if (!pageNode) throw new Error('Expected page node')
  const nodePagination = new NodePagination(pages, pageNode)
  const pagination = new ScriptPagination([nodePagination])
  const locking = PageLockMap.from(pages, document)
  const stubState: PaginationPluginState = {
    paginator: {
      init: (): NodePagination => new NodePagination([], pageNode),
      apply: (): NodePagination => new NodePagination([], pageNode),
    } satisfies Paginator,
    pagination,
    locking,
    decorations: DecorationSet.empty,
  }
  return new Plugin({
    key: paginationKey,
    providesPagination: true,
    state: {
      init: () => stubState,
      apply: (_tr, value) => value,
    },
  })
}

export function getPagination(
  editorState: EditorState,
): ScriptPagination | null {
  const pagination = paginationKey.getState(editorState)?.pagination
  return pagination instanceof ScriptPagination ? pagination : null
}

export function getPaginationPages(
  editorState: EditorState,
): BasePage[] | null {
  const pagination = paginationKey.getState(editorState)?.pagination
  return pagination?.pages ?? null
}

export function getLockedPageInventory(
  editorState: EditorState,
): PageLockMap | null {
  return paginationKey.getState(editorState)?.locking ?? null
}

export function isStartOfPage(editorState: EditorState, pos: number): boolean {
  return getPagination(editorState)?.pageAt(pos)?.start === pos
}

export function getPaginationLastPageShortfall(
  editorState: EditorState,
): number | null {
  return getPagination(editorState)?.lastPageShortfall ?? null
}

export function stripLockedPageBreaksFromSlice(slice: Slice): Slice {
  const children: Node[] = []
  slice.content.forEach((child) => {
    children.push(sanitizeNodeForPaste(child))
  })

  return new Slice(Fragment.fromArray(children), slice.openStart, slice.openEnd)
}

function sanitizeNodeForPaste(node: Node): Node {
  if (node.isText) return node

  const children: Node[] = []
  node.forEach((child) => {
    children.push(sanitizeNodeForPaste(child))
  })

  const nextAttrs =
    node.attrs.pageBreak === undefined
      ? node.attrs
      : {
          ...node.attrs,
          pageBreak: cleanLockedPageBreak(node.attrs.pageBreak),
        }

  return node.type.create(nextAttrs, Fragment.fromArray(children), node.marks)
}
